import { Alert, AlertDescription, AlertTitle } from "@/components/alert";
import { Button } from "@/components/button";
import { Combobox } from "@/components/combobox";
import CalendarDateRangePicker from "@/components/data-range-picker";
import { Label } from "@/components/label";
import QuestionMarkTooltip from "@/components/questionmark-tooltip";
import { Separator } from "@/components/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/tabs";
import { ToggleGroup, ToggleGroupItem } from "@/components/toggle-group";
import { CalendarDates } from "@/custom-types";
import { useAudienceAndCampaignOptions } from "@/http/api-hooks";
import { CampaignType, CampaignTypeValuesInstallOnly } from "@/http/api-types";
import { useUrlParams } from "@/http/use-url-params";
import { campaignTypeLabels } from "@/lib/mappings";
import { useFilterTracking } from "@/lib/mixpanel";
import { getAudienceSelectOptions, getCampaignSelectOptions } from "@/lib/utils";
import { MetricsNavigator } from "@/pages/analysis/metrics-navigator";
import { addDays } from "date-fns";
import { AlertTriangle } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import AnalysisCharts, { metricCategories, metricLabels, metrics } from "./analysis-charts";

const SPLIT_DIMENSIONS = [
  "all",
  "audience",
  "campaign",
  "prediction_flight_ids",
  "country",
  "partner",
  "ad_type",
  "placement_type",
  "identity",
  "device_type",
  "strategy_version_event_rate",
  "strategy_version_audience_similarity",
  "strategy_version_cpm",
] as const;
export type SplitDimension = (typeof SPLIT_DIMENSIONS)[number];

const splitDimensionOptions: { [key in SplitDimension]: string } = {
  all: "All",
  audience: "Audience",
  campaign: "Campaign",
  prediction_flight_ids: "Prediction Flights",
  country: "Country",
  partner: "Partner",
  ad_type: "Ad Type",
  placement_type: "Placement Type",
  identity: "Identity",
  device_type: "Device Type",
  strategy_version_event_rate: "ERP",
  strategy_version_audience_similarity: "ASP",
  strategy_version_cpm: "CPM",
};
export type AnalysisChartGranularity = "hourly" | "daily";

export type AggregationLevel = "daily" | "hourly" | "total";

function Analysis() {
  const { paramState, updateParam, updateDateRange, hasUrlParams } = useUrlParams({
    dimension: {
      name: "dimension",
      type: "enum",
      validValues: SPLIT_DIMENSIONS as readonly string[],
      defaultValue: "all" as SplitDimension,
    },
    audience: {
      name: "audience",
      type: "string",
      defaultValue: "",
    },
    campaign: {
      name: "campaign",
      type: "string",
      defaultValue: "",
    },
    campaignType: {
      name: "campaign_type",
      type: "enum",
      validValues: CampaignTypeValuesInstallOnly as readonly string[],
      defaultValue: "all" as CampaignType,
    },
    granularity: {
      name: "granularity",
      type: "enum",
      validValues: ["daily", "hourly"] as const,
      defaultValue: "daily" as AnalysisChartGranularity,
    },
    fromDate: {
      name: "from",
      type: "date",
      defaultValue: addDays(new Date(), -14),
    },
    toDate: {
      name: "to",
      type: "date",
      defaultValue: new Date(),
    },
  });

  const currentDate = {
    from: paramState.fromDate,
    to: paramState.toDate,
  };

  const dimension = paramState.dimension;
  const audience = paramState.audience;
  const granularity = paramState.granularity;
  const campaign = paramState.campaign;

  const { trackFilterChange } = useFilterTracking();

  const dateRangeIsSelected = !!currentDate?.from || !!currentDate?.to;

  const [readyToFetch, setReadyToFetch] = useState<boolean>(hasUrlParams && dateRangeIsSelected);

  const { data: audienceCampaignData, isLoading: audienceOptionsLoading } = useAudienceAndCampaignOptions({
    campaign_type: paramState.campaignType as string,
  });

  const shouldFetch = dateRangeIsSelected && readyToFetch;

  const onTabChange = (value: string) => {
    const newDimension = value as SplitDimension;

    if (dateRangeIsSelected) {
      trackFilterChange("dimension", newDimension);
      updateParam("dimension", newDimension);
    }
  };

  const audienceOptions = getAudienceSelectOptions(audienceCampaignData);

  const onAudienceChange = (value: string) => {
    trackFilterChange("audience", value);
    updateParam("campaign", "");
    updateParam("audience", value);
  };

  const onCampaignChange = (value: string) => {
    trackFilterChange("campaign", value);
    updateParam("campaign", value);
  };

  const onCampaignTypeChange = (value: string) => {
    trackFilterChange("campaign_type", value);
    updateParam("audience", "");
    updateParam("campaign", "");
    updateParam("campaignType", value);
  };

  const campaignOptions = getCampaignSelectOptions(audienceOptions, audience);

  const handleDateChange = (newDate: CalendarDates) => {
    trackFilterChange("date_range", { from: newDate.from, to: newDate.to });
    updateDateRange(newDate, "fromDate", "toDate");
  };

  const onGranularityChange = (value: AnalysisChartGranularity) => {
    trackFilterChange("granularity", value);
    updateParam("granularity", value);
  };

  const handleShowButtonClick = () => {
    setReadyToFetch(true);
    updateDateRange(currentDate, "fromDate", "toDate");
    updateParam("granularity", granularity);
  };

  // Prepare metrics data for the navigator
  const metricsForNavigator = metrics.map(metric => ({
    id: metric,
    label: metricLabels[metric],
    category: metricCategories[metric],
  }));

  const headerRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  useEffect(() => {
    const updateHeight = () => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.offsetHeight);
      }
    };
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  const showAllAudiencesOption = paramState.campaignType === "ctv_install";

  return (
    <>
      <div ref={headerRef} className="-mx-2 px-2 sticky top-0 z-40 bg-background pb-4">
        <div className="flex flex-col gap-4 mb-4">
          <div>
            <ToggleGroup
              type="single"
              value={paramState.campaignType as string}
              onValueChange={onCampaignTypeChange}
              variant="outline"
            >
              <ToggleGroupItem value="all" className="min-w-[100px]">
                {campaignTypeLabels.all}
              </ToggleGroupItem>
              <ToggleGroupItem value="install" className="min-w-[100px]">
                {campaignTypeLabels.install}
              </ToggleGroupItem>
              <ToggleGroupItem value="ctv_install" className="min-w-[100px]">
                {campaignTypeLabels.ctv_install}
              </ToggleGroupItem>
            </ToggleGroup>
          </div>

          <div className="flex justify-start space-x-8 overflow-x-auto">
            <div className="grid grid-cols-[260px_260px_260px_260px_auto] gap-3">
              <div>
                <Label className="font-semibold">Date</Label>
                <CalendarDateRangePicker className="w-full" date={currentDate} setDate={handleDateChange} />
              </div>
              <div>
                <Label className="font-semibold">Audience</Label>
                <Combobox
                  value={audience}
                  setValue={onAudienceChange}
                  isLoading={audienceOptionsLoading}
                  options={[
                    ...(showAllAudiencesOption ? [{ label: "All Audiences", value: "0" }] : []),
                    ...(audienceOptions
                      ? Object.keys(audienceOptions).map(audienceId => ({
                          label: audienceId + " - " + audienceOptions[audienceId].audience_name,
                          value: audienceId.toString(),
                        }))
                      : []),
                  ]}
                  disabled={!dateRangeIsSelected}
                  buttonPlaceholder="Select audience"
                  searchPlaceholder="Search audiences"
                  notFoundMessage="No audiences found"
                />
              </div>
              <div>
                <Label className="font-semibold">
                  Campaign{" "}
                  <QuestionMarkTooltip content="Select a specific campaign or leave empty for all campaigns." />
                </Label>
                <Combobox
                  disabled={!dateRangeIsSelected}
                  value={campaign}
                  setValue={onCampaignChange}
                  isLoading={audienceOptionsLoading}
                  options={
                    audienceOptions &&
                    campaignOptions.map(option => ({
                      label: option.id + " - " + option.name,
                      value: option.id.toString(),
                    }))
                  }
                  buttonPlaceholder="Select campaign"
                  searchPlaceholder="Search campaigns"
                  notFoundMessage="No campaigns found"
                />
              </div>
              <div>
                <Label className="font-semibold">
                  Granularity <QuestionMarkTooltip content="Select time frame granularity level." />
                </Label>
                <Combobox
                  disabled={false}
                  value={granularity}
                  setValue={onGranularityChange}
                  isLoading={audienceOptionsLoading}
                  options={[
                    { label: "Hour", value: "hourly" },
                    { label: "Day", value: "daily" },
                  ]}
                  buttonPlaceholder="Select granularity"
                  searchPlaceholder="Search granularity"
                  notFoundMessage="No granularity found"
                />
              </div>
              <div className="flex items-end align-baseline w-full">
                <Button onClick={handleShowButtonClick} disabled={!dateRangeIsSelected}>
                  Show
                </Button>
              </div>
            </div>
          </div>
        </div>
        <Separator />
      </div>
      <div className="flex flex-col lg:flex-row gap-6 w-full">
        <div className="flex-1 min-w-0 pb-20">
          <Tabs value={dimension} onValueChange={onTabChange} className="space-y-4">
            <TabsList className="sticky z-50 bg-muted" style={{ top: headerHeight }}>
              {SPLIT_DIMENSIONS.map(dim => (
                <TabsTrigger key={dim} value={dim}>
                  {splitDimensionOptions[dim]}
                </TabsTrigger>
              ))}
            </TabsList>
            <Alert className="mb-4 border-2">
              <AlertTriangle className="h-5 w-5" />
              <AlertTitle>Important</AlertTitle>
              <AlertDescription>
                All metrics in this analysis (except bids) are derived from events with tracked wins. If a win is not
                tracked, the related events are excluded.
              </AlertDescription>
            </Alert>
            <div className="flex gap-6">
              <div className="flex-1">
                {SPLIT_DIMENSIONS.map(dimensionTab => (
                  <TabsContent key={dimensionTab} value={dimensionTab} className="space-y-4 mt-0">
                    <AnalysisCharts
                      key={dimensionTab}
                      date={currentDate}
                      audience={audience}
                      campaign={campaign}
                      campaignType={paramState.campaignType as string}
                      dimension={dimensionTab}
                      granularity={granularity}
                      shouldFetch={shouldFetch && dimension === dimensionTab}
                    />
                  </TabsContent>
                ))}
              </div>
              {shouldFetch && dateRangeIsSelected && (
                <aside className="lg:w-72 lg:flex-shrink-0">
                  <div className="sticky z-10" style={{ top: (headerHeight ? headerHeight : 0) + 16 }}>
                    <MetricsNavigator
                      metrics={metricsForNavigator}
                      stickyOffset={(headerHeight ? headerHeight : 0) + 48 + 16}
                    />
                  </div>
                </aside>
              )}
            </div>
          </Tabs>
        </div>
      </div>
    </>
  );
}
export default Analysis;
